import React, { useState, useEffect, useCallback } from 'react';
import { Menu } from 'lucide-react';
import Sidebar from './Sidebar';

// Covers: iPhone, Android, iPad Mini, iPad Air, iPad Pro portrait
const TABLET_BREAKPOINT = 1024;

const DashboardLayout = ({ activeSection, onSectionChange, children, role = 'advisor' }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isCompact, setIsCompact] = useState(typeof window !== 'undefined' && window.innerWidth < TABLET_BREAKPOINT);

  useEffect(() => {
    const handleResize = () => {
      const compact = window.innerWidth < TABLET_BREAKPOINT;
      setIsCompact(compact);
      if (!compact) setDrawerOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSectionChange = useCallback((section) => {
    onSectionChange(section);
    if (isCompact) setDrawerOpen(false);
  }, [onSectionChange, isCompact]);

  const sidebarWidth = isCompact ? 0 : (collapsed ? 64 : 240);

  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        overflow: 'hidden',
        background: '#FCFCFC',
      }}
    >
      {/* Backdrop overlay when drawer is open */}
      {isCompact && drawerOpen && (
        <div
          onClick={() => setDrawerOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            zIndex: 40,
          }}
        />
      )}

      {/* Sidebar — off-screen drawer on mobile/tablet, fixed on desktop */}
      <div style={{
        position: 'fixed',
        left: isCompact ? (drawerOpen ? 0 : -280) : 0,
        top: 0,
        zIndex: 50,
        transition: 'left 0.25s cubic-bezier(0.4,0,0.2,1)',
      }}>
        <Sidebar
          activeSection={activeSection}
          onSectionChange={handleSectionChange}
          collapsed={isCompact ? false : collapsed}
          onToggle={() => isCompact ? setDrawerOpen(false) : setCollapsed(prev => !prev)}
          isDrawer={isCompact}
          onCloseDrawer={() => setDrawerOpen(false)}
          role={role}
        />
      </div>

      <main
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          marginLeft: sidebarWidth,
          transition: 'margin-left 0.25s cubic-bezier(0.4,0,0.2,1)',
          scrollBehavior: 'smooth',
        }}
      >
        {/* Hamburger top bar on mobile/tablet */}
        {isCompact && (
          <div
            className="no-print"
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 30,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '10px 16px',
              background: '#FFFFFF',
              borderBottom: '1px solid #E5E5E5',
            }}
          >
            <button
              onClick={() => setDrawerOpen(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: 6,
                border: '1px solid #E5E5E5',
                background: '#F5F5F5',
                cursor: 'pointer',
                color: '#525252',
              }}
            >
              <Menu size={20} />
            </button>
            <span style={{
              fontSize: 17,
              fontWeight: 600,
              color: '#1A1A1A',
              fontFamily: "'Outfit', sans-serif",
            }}>
              Miner<span style={{ color: '#92700C' }}>Ops</span>
            </span>
          </div>
        )}

        <div
          style={{
            maxWidth: 1600,
            margin: '0 auto',
            padding: isCompact ? '16px 12px' : 24,
          }}
        >
          {children}
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
